// ============================================================================
// QuickActionsFAB — Bouton hexagonal turquoise flottant et déplaçable
// Menu au survol : veille, actions, assistance (contact unifié), audit Qualiopi,
// documents légaux (liens externes cecodev.fr).
// Remplace : SupportFAB + VeilleQuickAccess + ActionQuickAccess
// ============================================================================

import { useState, useRef, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  MenuItem as MuiMenuItem,
  Chip,
  Drawer,
  ClickAwayListener,
  IconButton,
} from "@mui/material";
import {
  Scale,
  GraduationCap,
  Lightbulb,
  ClipboardPlus,
  Plus,
  X,
  Send,
  MessageCircle,
  ExternalLink,
  BookOpen,
  FileText,
  ShieldCheck,
  Bell,
  AlertTriangle,
  CheckCircle2,
  UserPlus,
  Store,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useCan } from "../hooks/usePermissions";
import { useProposerInter } from "../hooks/useProposerInter";
import { useCandidatureAnimateur } from "../hooks/useCandidatureAnimateur";
import { EXTERNAL_LINKS } from "../lib/external-links";
import { ContactForm, TYPE_CONFIG, type DemandeType } from "./ContactForm";
import { RetourBetaDialog } from "./RetourBetaWidget";
import { useIsMobile } from "../hooks/useIsMobile";
import { DialogMobile } from "./ui/DialogMobile";
import ChampDate from "./ChampDate";

// ============================================================================
// Couleurs & constantes
// ============================================================================

const TURQUOISE = "#4BB7A5";
const TURQUOISE_DARK = "#3A9A8A";
const STORAGE_KEY = "cecodev-fab-position";
const HEX_CLIP = "polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%)";

const VEILLE_TYPES = [
  {
    key: "LEGALE" as const,
    label: "Veille Légale",
    icon: Scale,
    color: "#2563eb",
    bgColor: "#dbeafe",
    indicateur: "I23",
    placeholder: "Ex : Décret n°2024-xxx, Loi formation professionnelle...",
  },
  {
    key: "COMPETENCES" as const,
    label: "Veille Compétences",
    icon: GraduationCap,
    color: "#16a34a",
    bgColor: "#dcfce7",
    indicateur: "I24",
    placeholder: "Ex : Évolution métier codéveloppement, nouvelles compétences...",
  },
  {
    key: "PEDAGOGIQUE" as const,
    label: "Veille Pédagogique",
    icon: Lightbulb,
    color: "#d97706",
    bgColor: "#fef3c7",
    indicateur: "I25",
    placeholder: "Ex : Nouveau outil IA, innovation pédagogique...",
  },
] as const;

type VeilleType = (typeof VEILLE_TYPES)[number]["key"];

const DOMAINES = [
  { value: "RECHERCHE", label: "Recherche" },
  { value: "PEDAGOGIE", label: "Pédagogie" },
  { value: "INTELLIGENCE_COLLECTIVE", label: "Intelligence Collective" },
  { value: "COMMUNICATION", label: "Communication" },
  { value: "COMMERCIAL", label: "Commercial" },
  { value: "OPERATIONS_INTRA", label: "Opérations INTRA" },
  { value: "QUALITE", label: "Qualité" },
  { value: "ADMINISTRATIF", label: "Administratif" },
];

const CAMPAGNES = ["AUDIT_2025", "AUDIT_2026", "AUDIT_2027"];

// Raccourcis "Assistance" exposés dans le menu hexagonal. La page publique
// /contact expose en plus RENSEIGNEMENT (non exposé ici car le FAB cible des
// utilisateurs déjà connectés, donc déjà en contact avec la plateforme).
// BUG_TECHNIQUE retiré du FAB (FX 2026-09-15) : pendant la bêta les anomalies
// passent par « Suggestion » (canal retours_beta, exportable et traité en lot
// avec l'IA) — les bugs déposés au support étaient inexportables.
const RACCOURCIS_CONTACT: DemandeType[] = [
  "PEDAGOGIQUE_COMMERCIAL",
  "SUGGESTION",
  "INCIDENT_SEANCE",
];

// Documents généraux hébergés sur cecodev.fr (ouverts en nouvel onglet)
const DOCS_EXTERNES: Array<{ label: string; href: string; icon: typeof BookOpen }> = [
  { label: "Règlement intérieur", href: EXTERNAL_LINKS.reglementInterieur, icon: BookOpen },
  // CGV servies par l'app (/cgv = version acceptée au tunnel), pas la vitrine.
  { label: "Conditions générales de vente", href: "/cgv", icon: FileText },
  { label: "Mentions légales", href: EXTERNAL_LINKS.mentionsLegales, icon: ShieldCheck },
];

// ============================================================================
// Menu item helper
// ============================================================================

function FabMenuItem({
  icon,
  label,
  sublabel,
  chip,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  chip?: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 0.75,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        "&:hover": disabled ? {} : { bgcolor: "#f5f5f5" },
        transition: "background-color 0.15s",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, flexShrink: 0 }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={500} noWrap>
          {label}
        </Typography>
        {sublabel && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {sublabel}
          </Typography>
        )}
      </Box>
      {chip && <Chip label={chip} size="small" sx={{ fontSize: 10, height: 18 }} />}
    </Box>
  );
}

// ============================================================================
// Composant principal
// ============================================================================

const FAB_SIZE = 56;
const MENU_WIDTH = 320;
const MENU_GAP = 12;

/**
 * Où ouvrir le menu sur ordinateur (FX 2026-10-13 : avant, ancré au-dessus de
 * l'hexagone sans regarder la place — coupé en haut, injoignable). Au-dessus
 * s'il y a plus de place qu'en dessous, aligné sur le bord de l'hexagone qui
 * laisse la place ; hauteur bornée par l'écran, défilement interne.
 */
function placerMenu(pos: { x: number; y: number }, vw: number, vh: number) {
  const placeDessus = pos.y - 8;
  const placeDessous = vh - (pos.y + FAB_SIZE) - 8;
  const dessus = placeDessus >= placeDessous;
  const maxHeight = Math.max(160, Math.min(Math.round(vh * 0.7), (dessus ? placeDessus : placeDessous) - MENU_GAP));
  const alignDroite = pos.x + FAB_SIZE - MENU_WIDTH >= 8 || pos.x + MENU_WIDTH > vw - 8;
  return { dessus, maxHeight, alignDroite };
}

export function QuickActionsFAB() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Action contextuelle animateur Inter (Phase 3, 2026-08-06 — MENU_CIBLE §6) :
  // AVANT habilitation → candidater ; APRÈS → proposer (logique partagée
  // useProposerInter — bascule d'org incluse si nécessaire).
  // Verrou badge inclus (FX 2026-10-13) : même règle que le menu.
  const candidature = useCandidatureAnimateur();
  const proposerInter = useProposerInter();
  const isMobile = useIsMobile();

  // ---- Menu state ----
  const [menuOpen, setMenuOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // Phase bêta (FX 2026-08-31) : dialog retours bêta ouvert par le raccourci
  // « Faire une suggestion ». ⚠ Hook déclaré ICI, avec les autres, AVANT les
  // early returns `if (!user)` / `if (!pos)` — placé plus bas il violait la
  // règle des hooks et démontait toute l'app (page blanche, incident
  // 2026-08-31 : « more hooks than during the previous render »).
  const [retourBetaOpen, setRetourBetaOpen] = useState(false);

  // ---- Position (left, top) ----
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const isPointerDownRef = useRef(false);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ px: 0, py: 0, ox: 0, oy: 0 });

  // Initialiser la position au montage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const p = JSON.parse(saved);
        if (typeof p.x === "number" && typeof p.y === "number") {
          // Clamp au viewport (incident 2026-08-31 : une position sauvegardée
          // hors écran — drag pendant un crash, changement de résolution —
          // rendait le bouton invisible pour toujours).
          setPos({
            x: Math.min(Math.max(0, p.x), window.innerWidth - FAB_SIZE),
            y: Math.min(Math.max(0, p.y), window.innerHeight - FAB_SIZE),
          });
          return;
        }
      }
    } catch { /* ignore */ }
    // Défaut : milieu bord droit
    setPos({
      x: window.innerWidth - FAB_SIZE - 16,
      y: Math.round(window.innerHeight / 2 - FAB_SIZE / 2),
    });
  }, []);

  // Échap ferme le menu (FX 2026-10-13).
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  // Un redimensionnement (rotation du téléphone, fenêtre réduite) garde
  // l'hexagone dans l'écran.
  useEffect(() => {
    const onResize = () =>
      setPos((p) =>
        p
          ? {
              x: Math.min(Math.max(0, p.x), window.innerWidth - FAB_SIZE),
              y: Math.min(Math.max(0, p.y), window.innerHeight - FAB_SIZE),
            }
          : p,
      );
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ---- Active dialog ----
  const [activeDialog, setActiveDialog] = useState<
    | null
    | { type: "veille"; veilleType: VeilleType }
    | { type: "action" }
    | { type: "contact"; demandeType: DemandeType }
  >(null);

  // ---- Veille form ----
  const [veilleSource, setVeilleSource] = useState("");
  const [veilleObjet, setVeilleObjet] = useState("");
  const [veilleDesc, setVeilleDesc] = useState("");
  const [veilleAction, setVeilleAction] = useState("");
  const [veilleDate, setVeilleDate] = useState(() => new Date().toISOString().split("T")[0]);

  // ---- Action form ----
  const [actCampagne, setActCampagne] = useState("AUDIT_2026");
  const [actIndicateurId, setActIndicateurId] = useState("");
  const [actDomaine, setActDomaine] = useState("");
  const [actDesc, setActDesc] = useState("");
  const [actResponsable, setActResponsable] = useState("");
  const [actPriorite, setActPriorite] = useState("MOYENNE");
  const [collaborateurs, setCollaborateurs] = useState<{ id: string; prenom: string | null; nom: string | null; role: string }[]>([]);
  const [indicateurs, setIndicateurs] = useState<{ id: string; numero: number; critere: number; libelle: string }[]>([]);

  // ---- Loading ----
  const [saving, setSaving] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  // ---- Notifications in-app (Sprint 6) ----
  interface NotifInApp {
    id: string;
    type: string;
    severite: "INFO" | "WARNING" | "CRITICAL";
    titre: string;
    message: string;
    lienHref: string | null;
    luLe: string | null;
    createdAt: string;
  }
  const [notifs, setNotifs] = useState<NotifInApp[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Polling léger : charge les notifs à l'ouverture du menu + toutes les 2 min
  useEffect(() => {
    let cancelled = false;
    async function tick() {
      try {
        const res = await api.get<{ count: number }>("/notifications/unread-count");
        if (!cancelled) setUnreadCount(res.data?.count ?? 0);
      } catch {
        /* silencieux */
      }
    }
    tick();
    const interval = setInterval(tick, 120 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Charger la liste quand on ouvre le menu
  useEffect(() => {
    if (!menuOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<NotifInApp[]>("/notifications?limit=20");
        if (!cancelled) setNotifs(res.data ?? []);
      } catch {
        /* silencieux */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [menuOpen]);

  async function markRead(notifId: string) {
    try {
      await api.patch(`/notifications/${notifId}/read`);
      setNotifs((prev) => prev.map((n) => (n.id === notifId ? { ...n, luLe: new Date().toISOString() } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      /* silencieux */
    }
  }
  async function markAllRead() {
    try {
      await api.post(`/notifications/read-all`);
      const now = new Date().toISOString();
      setNotifs((prev) => prev.map((n) => ({ ...n, luLe: now })));
      setUnreadCount(0);
    } catch {
      /* silencieux */
    }
  }

  if (!user) return null;
  if (!pos) return null; // Attendre l'initialisation de la position

  const isStaff = user.roles.some((r) => r === "SUPER_ADMIN" || r === "PILOTE");
  const isSuperAdmin = user.roles.includes("SUPER_ADMIN");

  // ==== Plateforme Inter — action contextuelle animateur (Phase 3) ====
  const showPostulerInter = candidature.visible;

  // ==== Hover : le menu s'ouvre/ferme sur l'HEXAGONE uniquement ====
  // Le container (hex + menu) garde le menu ouvert tant qu'on est dessus

  function handleHexMouseEnter() {
    if (isDraggingRef.current) return;
    clearTimeout(closeTimerRef.current);
    setMenuOpen(true);
  }

  function handleContainerMouseLeave() {
    closeTimerRef.current = setTimeout(() => setMenuOpen(false), 300);
  }

  function handleContainerMouseEnter() {
    // Annuler la fermeture si on entre sur le menu
    clearTimeout(closeTimerRef.current);
  }

  // ==== Drag : uniquement quand on appuie sur l'hexagone ====

  function handlePointerDown(e: React.PointerEvent) {
    isPointerDownRef.current = true;
    isDraggingRef.current = false;
    dragStartRef.current = { px: e.clientX, py: e.clientY, ox: pos?.x ?? 0, oy: pos?.y ?? 0 };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }

  function handlePointerMove(e: React.PointerEvent) {
    if (!isPointerDownRef.current) return; // <- fix : ne rien faire sans pointerDown
    const dx = e.clientX - dragStartRef.current.px;
    const dy = e.clientY - dragStartRef.current.py;
    // Seuil relevé à 10 px (FX 2026-10-13) : un toucher imprécis dépassait
    // 5 px, passait pour un glissement et poussait le bouton hors écran.
    if (!isDraggingRef.current && Math.abs(dx) + Math.abs(dy) > 10) {
      isDraggingRef.current = true;
      setMenuOpen(false);
    }
    if (isDraggingRef.current) {
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - FAB_SIZE, dragStartRef.current.ox + dx)),
        y: Math.max(0, Math.min(window.innerHeight - FAB_SIZE, dragStartRef.current.oy + dy)),
      });
    }
  }

  function handlePointerUp() {
    if (isDraggingRef.current) {
      // Sauvegarder la position
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
    } else if (isPointerDownRef.current) {
      // Clic / toucher sur l'hexagone = ouvre / ferme, partout (FX 2026-10-13 :
      // sur ordinateur le « x » ne fermait rien, seul le survol ouvrait).
      setMenuOpen((prev) => !prev);
    }
    isPointerDownRef.current = false;
    isDraggingRef.current = false;
  }

  // ==== Dialog helpers ====

  function closeDialog() {
    setActiveDialog(null);
  }

  function openVeilleDialog(veilleType: VeilleType) {
    setVeilleSource("");
    setVeilleObjet("");
    setVeilleDesc("");
    setVeilleAction("");
    setVeilleDate(new Date().toISOString().split("T")[0]);
    setActiveDialog({ type: "veille", veilleType });
    setMenuOpen(false);
  }

  function openActionDialog() {
    setActCampagne("AUDIT_2026");
    setActIndicateurId("");
    setActDomaine("");
    setActDesc("");
    setActResponsable("");
    setActPriorite("MOYENNE");
    setActiveDialog({ type: "action" });
    setMenuOpen(false);
    loadActionData();
  }

  // Phase bêta (FX 2026-08-31) : « Faire une suggestion » alimente le canal
  // retours_beta (URL de la page capturée, traitement en lot avec l'IA) au
  // lieu du support — les autres types restent des demandes qui appellent une
  // réponse. Fin de bêta : retirer cet aiguillage (état retourBetaOpen en tête
  // de composant).
  function openContactDialog(demandeType: DemandeType) {
    if (demandeType === "SUGGESTION") {
      setRetourBetaOpen(true);
      setMenuOpen(false);
      return;
    }
    setActiveDialog({ type: "contact", demandeType });
    setMenuOpen(false);
  }

  async function loadActionData() {
    try {
      const [collabRes, qualiopiRes] = await Promise.all([
        api.get("/qualiopi/collaborateurs"),
        api.get("/qualiopi", { params: { campagne: "AUDIT_2026" } }),
      ]);
      setCollaborateurs(collabRes.data);
      setIndicateurs(
        ((qualiopiRes.data.indicateurs || []) as Array<{ id: string; numero: number; critere: number; libelle: string }>).map((i) => ({
          id: i.id,
          numero: i.numero,
          critere: i.critere,
          libelle: i.libelle,
        }))
      );
    } catch (err) {
      console.error("Erreur chargement données:", err);
    }
  }

  // ==== Submit handlers ====

  async function handleVeilleSubmit() {
    if (!activeDialog || activeDialog.type !== "veille") return;
    if (!veilleSource.trim() || !veilleObjet.trim()) {
      toast.error("Source et objet sont requis");
      return;
    }
    try {
      setSaving(true);
      await api.post("/qualiopi/veille", {
        typeVeille: activeDialog.veilleType,
        dateVeille: veilleDate,
        source: veilleSource.trim(),
        objet: veilleObjet.trim(),
        description: veilleDesc.trim() || null,
        actionPrise: veilleAction.trim() || null,
      });
      toast.success("Veille enregistrée !");
      closeDialog();
    } catch (err) {
      console.error("Erreur veille:", err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  }

  async function handleActionSubmit() {
    if (!actDesc.trim()) {
      toast.error("Le libellé est requis");
      return;
    }
    try {
      setSaving(true);
      let suiviId: string | null = null;
      if (actIndicateurId) {
        const suiviRes = await api.put(`/qualiopi/${actIndicateurId}`, { campagne: actCampagne });
        suiviId = suiviRes.data.id;
      }
      await api.post("/qualiopi/actions", {
        suiviId,
        campagne: actCampagne,
        domaine: actDomaine || null,
        description: actDesc.trim(),
        priorite: actPriorite,
        responsable: actResponsable || null,
      });
      toast.success("Point d'action créé !");
      closeDialog();
    } catch (err) {
      console.error("Erreur création action:", err);
      toast.error("Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  }

  async function handleAuditQualiopi() {
    setMenuOpen(false);
    try {
      setAuditLoading(true);
      await api.post("/admin/qualiopi-digest");
      toast.success("Digest Qualiopi envoyé ! Vérifiez votre boîte email.");
    } catch (err) {
      console.error("Erreur audit Qualiopi:", err);
      toast.error("Erreur lors de l'envoi du digest");
    } finally {
      setAuditLoading(false);
    }
  }

  // ==== Render: menu ====

  // Contenu du menu, commun à l'ordinateur (panneau ancré à l'hexagone) et au
  // téléphone (panneau bas pleine largeur — FX 2026-10-13).
  function renderMenuContenu() {
    return (
      <>
        {/* Sprint 6 — Notifications in-app */}
        <Box sx={{ px: 2, py: 0.75, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Bell size={14} color={unreadCount > 0 ? "#d97706" : "#64748b"} />
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              Notifications {unreadCount > 0 ? `(${unreadCount})` : ""}
            </Typography>
          </Box>
          {unreadCount > 0 && (
            <Typography
              variant="caption"
              onClick={markAllRead}
              sx={{ cursor: "pointer", color: "primary.main", "&:hover": { textDecoration: "underline" } }}
            >
              Tout marquer lu
            </Typography>
          )}
        </Box>
        {notifs.length === 0 ? (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.disabled">
              Aucune notification.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 220, overflowY: "auto" }}>
            {notifs.slice(0, 8).map((n) => {
              const sevColor =
                n.severite === "CRITICAL"
                  ? "#dc2626"
                  : n.severite === "WARNING"
                    ? "#d97706"
                    : "#0284c7";
              const SevIcon =
                n.severite === "CRITICAL" || n.severite === "WARNING"
                  ? AlertTriangle
                  : CheckCircle2;
              const isUnread = !n.luLe;
              return (
                <Box
                  key={n.id}
                  onClick={() => {
                    if (isUnread) markRead(n.id);
                    if (n.lienHref) {
                      setMenuOpen(false);
                      window.location.assign(n.lienHref);
                    }
                  }}
                  sx={{
                    display: "flex",
                    gap: 1,
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    bgcolor: isUnread ? "#fef9c3" : "transparent",
                    "&:hover": { bgcolor: isUnread ? "#fde68a" : "#f5f5f5" },
                    borderLeft: isUnread ? `3px solid ${sevColor}` : "3px solid transparent",
                  }}
                >
                  <SevIcon size={14} color={sevColor} style={{ marginTop: 2, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={isUnread ? 600 : 500} noWrap>
                      {n.titre}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.3 }}>
                      {n.message.length > 90 ? `${n.message.slice(0, 90)}…` : n.message}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
        <Divider sx={{ my: 1 }} />

        {/* Section Plateforme Inter — action contextuelle animateur (Phase 3) :
            Postuler AVANT habilitation, Proposer APRÈS. Masquée sinon (on
            masque une action non autorisée plutôt que la laisser échouer). */}
        {(showPostulerInter || proposerInter.visible) && (
          <>
            <Box sx={{ px: 2, py: 0.75 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Plateforme Inter
              </Typography>
            </Box>
            {proposerInter.visible ? (
              <FabMenuItem
                icon={<Store size={18} color={TURQUOISE} />}
                label="Proposer un groupe Inter"
                sublabel={proposerInter.necessiteBascule ? "Bascule sur Cecodev_Inter, puis propose" : "Nouvelle proposition sur la marketplace"}
                onClick={() => {
                  setMenuOpen(false);
                  void proposerInter.lancerProposition();
                }}
              />
            ) : (
              <FabMenuItem
                icon={<UserPlus size={18} color={TURQUOISE} />}
                label="Postuler comme animateur Inter"
                sublabel="Candidater pour proposer des groupes"
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/mon-espace/devenir-animateur");
                }}
              />
            )}
            <Divider sx={{ my: 1 }} />
          </>
        )}

        {/* Section Qualité — Staff only */}
        {isStaff && (
          <>
            <Box sx={{ px: 2, py: 0.75 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Qualité
              </Typography>
            </Box>
            {VEILLE_TYPES.map((vt) => {
              const Icon = vt.icon;
              return (
                <FabMenuItem
                  key={vt.key}
                  icon={<Icon size={18} color={vt.color} />}
                  label={vt.label}
                  sublabel={`Indicateur ${vt.indicateur}`}
                  onClick={() => openVeilleDialog(vt.key)}
                />
              );
            })}
            <FabMenuItem
              icon={<ClipboardPlus size={18} color="#16a34a" />}
              label="Point d'action"
              sublabel="Plan d'actions Qualiopi"
              onClick={openActionDialog}
            />
            {isSuperAdmin && (
              <FabMenuItem
                icon={
                  auditLoading ? (
                    <CircularProgress size={18} sx={{ color: TURQUOISE }} />
                  ) : (
                    <Send size={18} color={TURQUOISE} />
                  )
                }
                label="Audit Qualiopi"
                sublabel="Envoyer le digest maintenant"
                onClick={handleAuditQualiopi}
                disabled={auditLoading}
              />
            )}
            <Divider sx={{ my: 1 }} />
          </>
        )}

        {/* Section Nous contacter */}
        <Box sx={{ px: 2, py: 0.75 }}>
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            Nous contacter
          </Typography>
        </Box>
        {RACCOURCIS_CONTACT.map((key) => {
          const cfg = TYPE_CONFIG[key];
          const Icon = cfg.icon;
          return (
            <FabMenuItem
              key={key}
              icon={<Icon size={18} color={cfg.color} />}
              label={cfg.label}
              sublabel={cfg.description}
              onClick={() => openContactDialog(key)}
            />
          );
        })}
        <Box sx={{ px: 2, pt: 0.5, pb: 0.75 }}>
          <Typography
            component={RouterLink}
            to="/contact"
            onClick={() => setMenuOpen(false)}
            variant="caption"
            sx={{
              color: "primary.main",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            <MessageCircle size={12} /> Ouvrir la page Nous contacter
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Section Documents — liens externes cecodev.fr */}
        <Box sx={{ px: 2, py: 0.75 }}>
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            Documents (cecodev.fr)
          </Typography>
        </Box>
        {DOCS_EXTERNES.map((doc) => {
          const Icon = doc.icon;
          return (
            <Box
              key={doc.label}
              component="a"
              href={doc.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 0.75,
                textDecoration: "none",
                color: "text.primary",
                "&:hover": { bgcolor: "#f5f5f5" },
              }}
            >
              <Box sx={{ width: 28, display: "flex", justifyContent: "center" }}>
                <Icon size={18} color="#64748b" />
              </Box>
              <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }} noWrap>
                {doc.label}
              </Typography>
              <ExternalLink size={12} style={{ opacity: 0.5 }} />
            </Box>
          );
        })}
      </>
    );
  }

  // Ordinateur : panneau ancré à l'hexagone, placé selon la place disponible.
  function renderMenuOrdinateur() {
    if (!pos) return null;
    const { dessus, maxHeight, alignDroite } = placerMenu(pos, window.innerWidth, window.innerHeight);
    return (
      <Paper
        elevation={8}
        onMouseEnter={handleContainerMouseEnter}
        sx={{
          position: "absolute",
          ...(dessus ? { bottom: FAB_SIZE + MENU_GAP } : { top: FAB_SIZE + MENU_GAP }),
          ...(alignDroite ? { right: 0 } : { left: 0 }),
          width: MENU_WIDTH,
          maxWidth: "calc(100vw - 16px)",
          maxHeight,
          overflowY: "auto",
          borderRadius: 3,
          py: 1,
        }}
      >
        {renderMenuContenu()}
      </Paper>
    );
  }

  // ==== Render: dialogs ====

  function renderVeilleDialog() {
    if (!activeDialog || activeDialog.type !== "veille") return null;
    const typeCfg = VEILLE_TYPES.find((t) => t.key === activeDialog.veilleType)!;
    const Icon = typeCfg.icon;

    return (
      <DialogMobile open onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: typeCfg.bgColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon size={18} color={typeCfg.color} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {typeCfg.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Indicateur {typeCfg.indicateur} Qualiopi
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <ChampDate fullWidth={false}
              label="Date"
              size="small"
              value={veilleDate}
              onChange={(v) => setVeilleDate(v)}
              sx={{ maxWidth: 200 }}
            />
            <TextField
              label="Source"
              size="small"
              value={veilleSource}
              onChange={(e) => setVeilleSource(e.target.value)}
              placeholder="Ex : Netvibes, Légifrance, LinkedIn, Conférence..."
              required
            />
            <TextField
              label="Objet"
              size="small"
              value={veilleObjet}
              onChange={(e) => setVeilleObjet(e.target.value)}
              placeholder={typeCfg.placeholder}
              required
            />
            <TextField
              label="Description (optionnel)"
              multiline
              rows={2}
              size="small"
              value={veilleDesc}
              onChange={(e) => setVeilleDesc(e.target.value)}
              placeholder="Résumé ou notes..."
            />
            <TextField
              label="Action prise (optionnel)"
              multiline
              rows={2}
              size="small"
              value={veilleAction}
              onChange={(e) => setVeilleAction(e.target.value)}
              placeholder="Action entreprise suite à cette veille..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleVeilleSubmit}
            disabled={saving || !veilleSource.trim() || !veilleObjet.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : <Plus size={16} />}
            sx={{ bgcolor: typeCfg.color, "&:hover": { bgcolor: typeCfg.color, filter: "brightness(0.9)" } }}
          >
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogActions>
      </DialogMobile>
    );
  }

  function renderActionDialog() {
    if (!activeDialog || activeDialog.type !== "action") return null;

    return (
      <DialogMobile open onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: "#dcfce7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ClipboardPlus size={18} color="#16a34a" />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Nouveau point d'action
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Plan d'actions Qualiopi
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                select
                label="Campagne"
                size="small"
                value={actCampagne}
                onChange={(e) => setActCampagne(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                {CAMPAGNES.map((c) => (
                  <MuiMenuItem key={c} value={c}>
                    {c.replace("_", " ")}
                  </MuiMenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Priorité"
                size="small"
                value={actPriorite}
                onChange={(e) => setActPriorite(e.target.value)}
                sx={{ minWidth: 130 }}
              >
                <MuiMenuItem value="CRITIQUE">Critique</MuiMenuItem>
                <MuiMenuItem value="HAUTE">Haute</MuiMenuItem>
                <MuiMenuItem value="MOYENNE">Moyenne</MuiMenuItem>
                <MuiMenuItem value="BASSE">Basse</MuiMenuItem>
              </TextField>
            </Box>
            <TextField
              select
              label="Indicateur (facultatif)"
              size="small"
              value={actIndicateurId}
              onChange={(e) => setActIndicateurId(e.target.value)}
              fullWidth
            >
              <MuiMenuItem value="">
                <em>Aucun — en attente d'affectation</em>
              </MuiMenuItem>
              {indicateurs.map((ind) => (
                <MuiMenuItem key={ind.id} value={ind.id}>
                  I{ind.numero} (C{ind.critere}) — {ind.libelle.substring(0, 60)}
                  {ind.libelle.length > 60 ? "..." : ""}
                </MuiMenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Domaine"
              size="small"
              value={actDomaine}
              onChange={(e) => setActDomaine(e.target.value)}
              fullWidth
            >
              <MuiMenuItem value="">
                <em>Non spécifié</em>
              </MuiMenuItem>
              {DOMAINES.map((d) => (
                <MuiMenuItem key={d.value} value={d.value}>
                  {d.label}
                </MuiMenuItem>
              ))}
            </TextField>
            <TextField
              label="Libellé du point d'action"
              size="small"
              value={actDesc}
              onChange={(e) => setActDesc(e.target.value)}
              required
              multiline
              rows={2}
              placeholder="Décrire l'action à mener..."
            />
            <TextField
              select
              label="Responsable"
              size="small"
              value={actResponsable}
              onChange={(e) => setActResponsable(e.target.value)}
              fullWidth
            >
              <MuiMenuItem value="">
                <em>Non attribué</em>
              </MuiMenuItem>
              {collaborateurs.map((c) => (
                <MuiMenuItem key={c.id} value={`${c.prenom || ""} ${c.nom || ""}`.trim()}>
                  {c.prenom} {c.nom} ({c.role})
                </MuiMenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleActionSubmit}
            disabled={saving || !actDesc.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : <ClipboardPlus size={16} />}
            sx={{ bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}
          >
            {saving ? "Création..." : "Créer le point d'action"}
          </Button>
        </DialogActions>
      </DialogMobile>
    );
  }

  function renderContactDialog() {
    if (!activeDialog || activeDialog.type !== "contact") return null;
    const cfg = TYPE_CONFIG[activeDialog.demandeType];
    const Icon = cfg.icon;

    return (
      <Dialog open onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                bgcolor: `${cfg.color}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon size={20} color={cfg.color} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {cfg.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {cfg.description}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <ContactForm
              initialType={activeDialog.demandeType}
              showHeader={false}
              onSuccess={closeDialog}
            />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // ==== Render principal ====

  // Le menu s'ouvre au-dessus de l'hex. Pour que le hover reste actif
  // quand la souris passe de l'hex au menu, on utilise un container unique
  // avec une zone invisible qui fait le pont entre les deux.

  // Ordinateur : menu ancré ; téléphone : panneau bas (Drawer), jamais le menu ancré.
  const placement = !isMobile && menuOpen ? placerMenu(pos, window.innerWidth, window.innerHeight) : null;

  return (
    <>
      <ClickAwayListener
        onClickAway={() => {
          if (!isMobile && menuOpen) setMenuOpen(false);
        }}
      >
      {/* Zone de hover : couvre hex + menu, position fixe */}
      <Box
        onMouseEnter={handleContainerMouseEnter}
        onMouseLeave={handleContainerMouseLeave}
        sx={{
          position: "fixed",
          left: pos.x,
          top: pos.y,
          zIndex: 1300,
          width: FAB_SIZE,
          height: FAB_SIZE,
          // Le menu (position absolute) dépasse : ne pas le couper
          overflow: "visible",
          // Agrandir la zone de hover invisible vers le haut pour
          // couvrir le gap entre l'hex et le menu
          "&::before": placement
            ? {
                content: '""',
                position: "absolute",
                ...(placement.dessus ? { bottom: FAB_SIZE } : { top: FAB_SIZE }),
                ...(placement.alignDroite ? { right: 0 } : { left: 0 }),
                width: MENU_WIDTH,
                height: MENU_GAP + 4, // pont invisible hex ↔ menu
              }
            : undefined,
        }}
      >
        {/* Menu ancré (ordinateur), au-dessus ou en dessous selon la place */}
        {placement && renderMenuOrdinateur()}

        {/* Hexagone turquoise */}
        <Box
          onMouseEnter={handleHexMouseEnter}
          sx={{ filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.25))" }}
        >
          <Box
            role="button"
            tabIndex={0}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setMenuOpen((prev) => !prev);
              }
            }}
            sx={{
              width: FAB_SIZE,
              height: FAB_SIZE,
              clipPath: HEX_CLIP,
              bgcolor: menuOpen ? TURQUOISE_DARK : TURQUOISE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "grab",
              userSelect: "none",
              touchAction: "none",
              transition: "background-color 0.2s",
              "&:hover": { bgcolor: TURQUOISE_DARK },
              "&:focus-visible": {
                outline: `3px solid ${TURQUOISE}`,
                outlineOffset: 4,
              },
            }}
          >
            {menuOpen ? <X size={24} color="white" /> : <Plus size={24} color="white" />}
          </Box>
        </Box>
      </Box>
      </ClickAwayListener>

      {/* Téléphone : panneau bas pleine largeur (FX 2026-10-13) — le menu ancré
          sortait de l'écran ; l'hexagone (au-dessus du voile) ferme aussi. */}
      <Drawer
        anchor="bottom"
        open={isMobile && menuOpen}
        onClose={() => setMenuOpen(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: "80vh", pb: "env(safe-area-inset-bottom)" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Actions rapides</Typography>
          <IconButton size="small" aria-label="Fermer" onClick={() => setMenuOpen(false)}>
            <X size={18} />
          </IconButton>
        </Box>
        <Box sx={{ overflowY: "auto" }}>{renderMenuContenu()}</Box>
      </Drawer>

      {/* Dialogs */}
      {renderVeilleDialog()}
      {renderActionDialog()}
      {renderContactDialog()}
      <RetourBetaDialog open={retourBetaOpen} onClose={() => setRetourBetaOpen(false)} />
    </>
  );
}
